/**
 * Step 7 (Platform-Eng workflow) — PolicyDecisionDetailSlideout.
 *
 * <p>Drill-down view of a single {@code policy_decisions} row. Opened
 * from {@link PolicyDecisionsInventory} when the operator clicks a
 * row. Lays out the decision metadata in a clean two-column grid and
 * renders the {@code contextData} JSON (the input bundle the rule
 * actually evaluated against) as a pretty-printed, copy-able block.</p>
 *
 * <p>Production hygiene:</p>
 * <ul>
 *   <li>Decision pill is color-coded (success / danger / warning)
 *       matching the inventory table.</li>
 *   <li>Long ruleDetails / violationReason text wraps and stays
 *       readable.</li>
 *   <li>contextData copy button writes the JSON to clipboard so the
 *       SRE can paste it into a ticket or replay it through the
 *       smoke-eval endpoint.</li>
 *   <li>When the parent passes {@code loading=true} the body shows
 *       a spinner; on {@code error} a danger banner replaces the
 *       body. Both keep the slideout chrome stable so the close
 *       affordance is always present.</li>
 * </ul>
 *
 * <h3>Props</h3>
 *
 * <p>{@code isOpen}, {@code onClose}, {@code loading}, {@code error},
 * {@code detail}. {@code detail} is the JSON body returned by
 * {@code GET /api/v1/policy/decisions/{id}} — id, decision, agentId,
 * skillId/skillName, userEmail, ruleName, ruleDetails,
 * violationReason, contextData, createdAt.</p>
 */
import React, { useState, useCallback } from 'react';
import {
  Copy, CheckCircle, AlertTriangle, Loader2,
  ShieldCheck, ShieldX, ShieldAlert, FileJson,
} from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Card } from '../Card';
import { Pill } from '../Pill';

const DECISION_PILL = {
  allowed:   { tone: 'success', label: 'allowed',   icon: ShieldCheck },
  blocked:   { tone: 'danger',  label: 'blocked',   icon: ShieldX },
  escalated: { tone: 'warning', label: 'escalated', icon: ShieldAlert },
};

function formatTs(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); }
  catch { return String(iso); }
}

function Field({ label, value, mono = false, full = false }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">
        {label}
      </dt>
      <dd className={`mt-0.5 text-[12.5px] ${mono ? 'font-mono-v2 break-all' : ''} text-v2-ink`}>
        {value == null || value === '' ? '—' : value}
      </dd>
    </div>
  );
}

export function PolicyDecisionDetailSlideout({
  isOpen, onClose, loading = false, error = null, detail = null,
}) {
  const [copied, setCopied] = useState(false);

  const copyContext = useCallback(async () => {
    if (!detail?.contextData) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(detail.contextData, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  }, [detail]);

  const pill = detail
    ? (DECISION_PILL[detail.decision] || { tone: 'muted', label: detail.decision || '—' })
    : null;
  const PillIcon = pill?.icon;

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Policy decision detail"
      width={640}
    >
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FileJson size={16} className="text-v2-action-strong" />
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            Policy decision
          </h2>
          {detail && pill && (
            <Pill tone={pill.tone} size="sm" dot>
              {PillIcon && <PillIcon size={10} style={{ display: 'inline', marginRight: 4 }} />}
              {pill.label}
            </Pill>
          )}
        </div>

        {loading && (
          <div className="px-3 py-6 rounded-v2-sm bg-v2-surface-muted/30 text-[12px] text-v2-ink-muted flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading decision…
          </div>
        )}

        {!loading && error && (
          <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12px] text-v2-danger-strong flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && detail && (
          <>
            <Card title="Decision">
              <div className="p-3">
                <dl className="grid grid-cols-2 gap-3">
                  <Field label="Decision ID"      value={detail.id}            mono />
                  <Field label="Evaluated at"     value={formatTs(detail.createdAt)} mono />
                  <Field label="Policy type"      value={detail.policyType} />
                  <Field label="Rule name"        value={detail.ruleName} />
                  <Field label="User"             value={detail.userEmail || detail.userId} mono />
                  <Field label="Agent ID"         value={detail.agentId} mono />
                  <Field label="Skill name"       value={detail.skillName} />
                  <Field label="Skill ID"         value={detail.skillId} mono />
                  {detail.ruleDetails && (
                    <Field label="Rule details"     value={detail.ruleDetails} full />
                  )}
                  {detail.violationReason && (
                    <Field label="Violation reason" value={detail.violationReason} full />
                  )}
                </dl>
              </div>
            </Card>

            <Card>
              <Card.Header
                title="Input bundle (contextData)"
                actions={detail.contextData && (
                  <Button
                    variant="ghost" size="sm"
                    iconLeft={copied ? <CheckCircle size={12} /> : <Copy size={12} />}
                    onClick={copyContext}
                  >
                    {copied ? 'Copied' : 'Copy JSON'}
                  </Button>
                )}
              />
              <Card.Body>
                {!detail.contextData ? (
                  <p className="text-[12px] text-v2-ink-muted italic m-0">
                    No input bundle was recorded for this decision.
                  </p>
                ) : (
                  <pre className="px-3 py-2 rounded-v2-sm bg-v2-surface-muted text-[11px] font-mono-v2 text-v2-ink overflow-x-auto max-h-96">
                    {JSON.stringify(detail.contextData, null, 2)}
                  </pre>
                )}
              </Card.Body>
            </Card>
          </>
        )}

        <div className="flex items-center justify-end pt-2 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Slideout>
  );
}

export default PolicyDecisionDetailSlideout;
